import React, { useRef, useState, useEffect } from 'react';
import {
  Button,
  MenuItem,
  TextField,
  Autocomplete,
  Typography,
  useTheme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSelector } from 'react-redux';
import axios from '../MyAxios';

import FilterSelect from './FilterSelect';
import { ExpandMore as ExpandMoreIcon, FilterList } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import clsx from 'clsx';
import { isDateInvalid } from '@helpers';

const useStyles = makeStyles((theme) => ({
  filtersContainer: {
    borderWidth: 1,
    minWidth: 300,
  },
  border: {
    borderWidth: 1,
    borderColor: '#D1D1D1 !important',
  },
}));
function GroupFilter({ postType, setPostType, group, onChange }) {
  const [open, setOpen] = useState(false);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [localFromDate, setLocalFromDate] = useState(null);
  const [localToDate, setLocalToDate] = useState(null);

  const dateSelectorRef1 = useRef();
  const dateSelectorRef2 = useRef();

  // userRecord not needed here
  useSelector((state) => state.user);
  const theme = useTheme();

  const classes = useStyles();
  const handleToggle = () => {
    setOpen(!open);
    if (!open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'auto';
  };
  const filterIsApplied =
    (selectedTopics && selectedTopics.length) ||
    Boolean(localFromDate) ||
    Boolean(localToDate) ||
    Boolean(postType);
  const dateSelectorSlotProps = {
    field: {
      clearable: true,
    },
    textField: {
      size: 'small',
      placeholder: 'Select date',
      onKeyDown: (e) => e.preventDefault(),
      InputProps: {
        color: 'primary',
        size: 'small',
        classes: {
          notchedOutline: clsx(classes.border, 'rounded-md p-3'),
        },
      },
    },
  };

  const dateSelectorSlots = {
    textField: (props) => {
      const dateIsInvalid = isDateInvalid(props.value);

      return (
        <TextField
          {...props}
          value={dateIsInvalid ? '' : props.value}
          onClick={(e) => {
            if (e.target.nodeName === 'INPUT') {
              e.currentTarget.blur();
              e.currentTarget
                .closest('.MuiFormControl-root')
                ?.querySelector('.MuiIconButton-edgeEnd')
                ?.click();
            }
          }}
          inputProps={{ inputMode: 'none' }}
        />
      );
    },
  };
  const handleClear = () => {
    setSelectedTopics([]);
    // Dates
    setLocalFromDate(null);
    setLocalToDate(null);
    setPostType('');
    setOpen(false);
    document.body.style.overflow = 'auto';
  };

  const [tagsOptions, setTagsOptions] = useState(
    group?.tags?.map((t) => t.name) ?? [],
  );

  // Fetch group-specific topics when group changes
  useEffect(() => {
    let cancelled = false;
    const fetchGroupTags = async () => {
      if (!group?.id)
        return setTagsOptions(group?.tags?.map((t) => t.name) ?? []);
      try {
        const res = await axios.get(`/group/tags/${group.id}`);
        if (cancelled) return;
        const fetched = res.data?.tags || [];
        const names = fetched.map(
          (t) => t.name || t.subcategory || t.label || t,
        );
        setTagsOptions(names);
      } catch (err) {
        // fallback to existing group.tags shape
        setTagsOptions(group?.tags?.map((t) => t.name) ?? []);
      }
    };

    fetchGroupTags();
    return () => {
      cancelled = true;
    };
  }, [group?.id, group?.tags]);

  const hasMounted = useRef(false);
  // Notify parent when filters change (ensure date filter changes propagate)
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }
    onChange?.({
      selectedTags: selectedTopics,
      fromDate: localFromDate,
      toDate: localToDate,
      postType,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTopics, localFromDate, localToDate, postType]);

  return (
    <div className="flex flex-col items-end relative">
      <Button
        variant="outlined"
        size="small"
        sx={
          filterIsApplied
            ? {
                backgroundColor: '#caf7e3',
                boxShadow: `0 0 5px ${theme.palette.primary.light}`,
              }
            : { borderColor: '#CCCCCC' }
        }
        startIcon={<FilterList />}
        onClick={handleToggle}
      >
        <Typography className="normal-case">Filters</Typography>
      </Button>

      {open && (
        <div
          onClick={handleToggle}
          className="fixed h-screen w-screen z-10 left-0 top-0 overscroll-none"
        />
      )}

      <div
        className={clsx(
          'absolute right-0 top-10 rounded-xl bg-white border-[#D1D1D1] p-4 flex flex-col gap-4 shadow-md z-20',
          classes.filtersContainer,
          open ? 'block' : 'hidden',
        )}
        style={{ minWidth: 260 }}
      >
        <FilterSelect
          size="small"
          displayEmpty
          value={postType ?? ''}
          onChange={(e) => {
            setPostType(e.target.value);
            onChange?.({
              selectedTags: selectedTopics,
              fromDate: localFromDate,
              toDate: localToDate,
              postType: e.target.value,
            });
          }}
          renderValue={(selected) => {
            if (!selected) return 'Post Types'; // placeholder
            if (selected === 'all') return 'All';
            if (selected === 'post') return 'Regular';
            if (selected === 'event') return 'Event';
            return selected;
          }}
          IconComponent={(props) => <ExpandMoreIcon {...props} />}
        >
          {/* Only real selectable options */}
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="post">Regular</MenuItem>
          <MenuItem value="event">Event</MenuItem>
        </FilterSelect>

        <Autocomplete
          size="small"
          multiple
          clearIcon={null}
          disableCloseOnSelect
          options={tagsOptions}
          getOptionLabel={(option) => option}
          value={selectedTopics}
          onChange={(e, selected) => {
            setSelectedTopics(selected);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Topics"
              variant="outlined"
              placeholder="Search topics"
            />
          )}
        />

        <div ref={dateSelectorRef1}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              label="From"
              format="DD MMM, YYYY"
              className="w-full"
              value={localFromDate ? dayjs(localFromDate) : null}
              maxDate={
                localToDate ? dayjs(localToDate, 'YYYY-MM-DD') : undefined
              }
              // use onAccept so we only set the filter when the user confirms a date
              onAccept={(date) => {
                const formattedDate = date ? date.format('YYYY-MM-DD') : null;
                setLocalFromDate(formattedDate);
              }}
              slots={dateSelectorSlots}
              slotProps={{
                ...dateSelectorSlotProps,
                popper: {
                  anchorEl: dateSelectorRef1.current,
                },
              }}
            />
          </LocalizationProvider>
        </div>

        <div ref={dateSelectorRef2}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              label="To"
              format="DD MMM, YYYY"
              className="w-full"
              value={localToDate ? dayjs(localToDate) : null}
              minDate={
                localFromDate ? dayjs(localFromDate, 'YYYY-MM-DD') : undefined
              }
              // use onAccept so we only set the filter when the user confirms a date
              onAccept={(date) => {
                const formattedDate = date ? date.format('YYYY-MM-DD') : null;
                setLocalToDate(formattedDate);
              }}
              slots={dateSelectorSlots}
              slotProps={{
                ...dateSelectorSlotProps,
                popper: {
                  anchorEl: dateSelectorRef2.current,
                },
              }}
            />
          </LocalizationProvider>
        </div>

        <div className="flex justify-end">
          <Button
            variant="outlined"
            className="py-0.5 normal-case"
            onClick={handleClear}
          >
            Clear
          </Button>
        </div>
      </div>
    </div>
  );
}

export default GroupFilter;
